// @flow
import React from 'react';
import {Link} from 'react-router-dom';
import LabelInput from 'components/register/LabelInput';
import Button from 'components/common/Button';
import { isBrowser } from 'react-device-detect';

import './RegisterForm.scss';

type Props = {
  onChange(e: SyntheticInputEvent<HTMLInputElement>): void,
  onCheckBoxChange(e: SyntheticInputEvent<HTMLInputElement>): void,
  onAllCheckBox(e: SyntheticInputEvent<HTMLInputElement>): void,
  onRegister(): Promise<*>,
  managerName: string,
  displayName: string,
  email: string,
  password: string,
  mobile: string,
  bizNumber: string,
  shortBio: string,
  isAllAgree: boolean,
  isAdult: boolean,
  isTerms: boolean,
  isPrivacy: boolean,
  isPromotion: boolean,
  emailEditable: boolean,
  error: ?{
    name: string,
    payload: any,
  },
  hideEmail: boolean,
};

const errorMap = {
  DUPLICATED_ACCOUNT: (payload: string) =>
    `이미 존재하는 ${payload === 'email' ? '이메일' : '아이디'}입니다.`,
  FIELD_RULE: (payload: string) => {
    const rules = {
      email: '이메일은 소문자만 입력하세요.',
      password: '비밀번호는 6~30자로 입력하세요.',
      managerName: '이름을 1~20자로 입력하세요.',
      mobile: '핸드폰 번호는 숫자만 허용됩니다.',
      isAdult: '만 14세 이상이여야 합니다.',
      isTerms: '이용약관 동의 체크해주세요.',
      isPrivacy: '개인정보처리방침 동의 체크해주세요.',
    };
    return rules[payload] || '입력한 데이터가 문제가 있습니다.';
  },
};

const printError = (error: { name: string, payload: any }) => {
  if (!errorMap[error.name]) {
    return '회원가입이 실패하였습니다. 고객센터(02-585-1342)에 문의 해주시기 바랍니다.';
  }
  return errorMap[error.name](error.payload);
};

const RegisterForm = ({
  onChange,
  onCheckBoxChange,
  onAllAgree,
  onAllAgreeBtn,
  onRegister,
  managerName,
  displayName,
  email,
  password,
  mobile,
  bizNumber,
  isAllAgree,
  isAdult,
  isTerms,
  isPrivacy,
  isPromotion,
  error,
}: Props) => {
  return (
    <div className="register-form">
      <div className="form-head">
        <h2>회원가입</h2>
      </div>
      <div className="form-contents">
        <LabelInput
          type="email"
          value={email}
          name="email"
          required
          label="이메일"
          placeholder="이메일을 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          type="password"
          value={password}
          name="password"
          required
          label="비밀번호"
          placeholder="6자 이상 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          value={mobile}
          name="mobile"
          required
          label="핸드폰 번호"
          placeholder="핸드폰 번호를 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          value={managerName}
          name="managerName"
          required
          label="이름"
          placeholder="이름을 입력해주세요. ex)최영진"
          onChange={onChange}
        />
        <LabelInput
          value={displayName}
          name="displayName"
          label="회사 이름"
          placeholder="회사 이름을 입력해주세요. ex)캐시바이, 데이터씨"
          onChange={onChange}
        />
        <LabelInput
          value={bizNumber}
          name="bizNumber"
          label="사업자 등록 번호"
          placeholder="사업자 등록 번호를 입력해주세요."
          onChange={onChange}
        />
        <div className="register-form-group">
          <div className="register-form-group-label">
            약관 동의
          </div>
          <div className="register-form-group-input">
            <div className="register-terms-agree">
              <div className="register-terms-agree-all">
                <div className="form-check-checkbox-input">
                  <div className="form-check-label" onClick={onAllAgreeBtn}>
                    <input
                      type="checkbox"
                      name="isAllAgree"
                      checked={isAllAgree}
                      onChange={onAllAgree}
                      className="check-img"
                    />
                    <span className="register-terms-agree-all-text">전체동의</span>
                  </div>
                </div>
              </div>
              <div className="register-terms-agree-list">
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isAdult"
                        checked={isAdult}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">만 14세 이상입니다.
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isTerms"
                        checked={isTerms}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        <a className="register-terms-agree-link" href="/policy/terms" target="_blank">캐시바이 이용약관</a>
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isPrivacy"
                        checked={isPrivacy}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        <a className="register-terms-agree-link" href="/policy/privacy" target="_blank">
                          개인정보 수집 및 이용
                        </a>
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isPromotion"
                        checked={isPromotion}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">개인정보 활용 및 마케팅 정보 수신 동의
                        <span className="register-terms-agree-chose">(선택)</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {error && <div className="error">{printError(error)}</div>}
        <Button fullWidth large theme="default" color="blue" onClick={onRegister} desktop={isBrowser}>
          회원가입 완료
        </Button>
        <div className="guideToLogin">
          <span>이미 아이디가 있으신가요?</span>
          <Link to={'/login'}>로그인</Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;



// WEBPACK FOOTER //
// ./src/components/register/RegisterForm/RegisterForm.js